import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft,
  Plus,
  Trash2,
  Upload,
  Film,
  AudioLines,
  Loader2,
  Sparkles,
  User as UserIcon,
  Clapperboard,
  Download,
  X,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_LABELS = {
  queued: 'Queued',
  planning: 'Planning scenes',
  generating_clips: 'Generating clips',
  generating_clip_1: 'Generating clip 1/3',
  generating_clip_2: 'Generating clip 2/3',
  generating_clip_3: 'Generating clip 3/3',
  generating_clip_4: 'Generating clip 4/4',
  stitching: 'Stitching clips',
  adding_audio: 'Generating voiceover',
  complete: 'Ready',
  error: 'Error',
};

const VideoStudio = ({ onBack, user }) => {
  const [characters, setCharacters] = useState([]);
  const [voices, setVoices] = useState([]);
  const [projects, setProjects] = useState([]);
  const [activeProject, setActiveProject] = useState(null);
  const [showCharModal, setShowCharModal] = useState(false);

  // create project form
  const [title, setTitle] = useState('');
  const [script, setScript] = useState('');
  const [pickedChars, setPickedChars] = useState([]); // up to 2
  const [mode, setMode] = useState('slideshow'); // 'sora' | 'slideshow'
  const [totalSeconds, setTotalSeconds] = useState(30);
  const [submitting, setSubmitting] = useState(false);

  const pollRef = useRef(null);

  // ---------- data load -----------------------------------------------------
  const loadCharacters = async () => {
    try {
      const r = await axios.get(`${API}/characters`);
      setCharacters(r.data.characters || []);
    } catch (e) {
      toast.error('Failed to load characters');
    }
  };
  const loadVoices = async () => {
    try {
      const r = await axios.get(`${API}/voices`);
      setVoices(r.data.voices || []);
    } catch (e) {
      /* noop */
    }
  };
  const loadProjects = async () => {
    try {
      const r = await axios.get(`${API}/video/projects`);
      setProjects(r.data.projects || []);
    } catch (e) {
      /* noop */
    }
  };

  useEffect(() => {
    loadCharacters();
    loadVoices();
    loadProjects();
    // Pick up any prefill from Calendar "Generate Reel from this script"
    try {
      const raw = localStorage.getItem('studio_prefill');
      if (raw) {
        const data = JSON.parse(raw);
        if (data?.script) {
          setTitle(data.title || '');
          setScript(data.script);
          toast.success('Script loaded from Calendar');
        }
        localStorage.removeItem('studio_prefill');
      }
    } catch (e) {
      /* noop */
    }
  }, []);

  // ---------- polling for active project ----------------------------------
  useEffect(() => {
    if (!activeProject) return;
    if (['complete', 'error'].includes(activeProject.status) && activeProject.final_video_url) {
      return;
    }
    pollRef.current = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/video/projects/${activeProject.id}`);
        setActiveProject(r.data);
        if (['complete', 'error'].includes(r.data.status) && (r.data.final_video_url || !r.data.audio_url)) {
          loadProjects();
          clearInterval(pollRef.current);
        }
      } catch (e) {
        /* keep trying */
      }
    }, 4000);
    return () => clearInterval(pollRef.current);
  }, [activeProject?.id, activeProject?.status]);

  // ---------- character actions -------------------------------------------
  const togglePickChar = (id) => {
    setPickedChars((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= 2) {
        toast.error('Max 2 characters per clip');
        return prev;
      }
      return [...prev, id];
    });
  };

  const handleDeleteChar = async (id) => {
    if (!window.confirm('Delete this character?')) return;
    try {
      await axios.delete(`${API}/characters/${id}`);
      setPickedChars((prev) => prev.filter((x) => x !== id));
      loadCharacters();
      toast.success('Character deleted');
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  // ---------- submit project ----------------------------------------------
  const handleSubmit = async () => {
    if (!script.trim()) return toast.error('Script is required');
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/video/projects`, {
        title: title || 'Untitled clip',
        script,
        character_ids: pickedChars,
        mode,
        total_seconds: totalSeconds,
      });
      toast.success('Generation started');
      setActiveProject(r.data);
      setTitle('');
      setScript('');
      setPickedChars([]);
      loadProjects();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to start');
    } finally {
      setSubmitting(false);
    }
  };

  const handleGenerateAudio = async () => {
    if (!activeProject) return;
    try {
      await axios.post(`${API}/video/projects/${activeProject.id}/audio`);
      toast.success('Voiceover generation started');
      const r = await axios.get(`${API}/video/projects/${activeProject.id}`);
      setActiveProject(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Audio generation failed');
    }
  };

  const handleDeleteProject = async (id) => {
    if (!window.confirm('Delete this project and its files?')) return;
    try {
      await axios.delete(`${API}/video/projects/${id}`);
      if (activeProject?.id === id) setActiveProject(null);
      loadProjects();
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  const renderVideoUrl = (p) => {
    if (!p) return null;
    const url = p.final_video_url || p.video_url;
    return url ? `${BACKEND_URL}${url}` : null;
  };

  const renderAudioUrl = (p) => (p?.audio_url ? `${BACKEND_URL}${p.audio_url}` : null);

  return (
    <div className="min-h-screen bg-[#0F0F1A] text-white" data-testid="video-studio-page">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-[#0F0F1A]/95 backdrop-blur border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-4">
          <button
            onClick={onBack}
            className="p-2 rounded-full hover:bg-white/5 text-white/70"
            data-testid="studio-back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <Clapperboard className="w-6 h-6 text-[#FF7F00]" />
          <div>
            <h1 className="text-lg font-bold tracking-tight">Video Studio</h1>
            <p className="text-xs text-white/50">Sora 2 Reels • OpenAI TTS voiceover</p>
          </div>
          <div className="ml-auto text-xs text-white/40">
            {user?.email}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8 grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left column — characters + projects */}
        <div className="lg:col-span-1 space-y-8">
          {/* Character library */}
          <section data-testid="character-library">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold uppercase tracking-wider text-white/70">
                Character Library
              </h2>
              <button
                onClick={() => setShowCharModal(true)}
                className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-[#FF7F00] hover:bg-[#FF9933] text-black font-semibold"
                data-testid="add-character-btn"
              >
                <Plus className="w-3.5 h-3.5" /> Add
              </button>
            </div>
            <div className="space-y-3">
              {characters.length === 0 && (
                <div className="text-xs text-white/40 border border-dashed border-white/15 rounded-lg p-6 text-center">
                  No characters yet. Add a face + voice to start.
                </div>
              )}
              {characters.map((c) => {
                const picked = pickedChars.includes(c.id);
                return (
                  <div
                    key={c.id}
                    className={`group flex items-center gap-3 p-3 rounded-lg border transition-all cursor-pointer ${
                      picked
                        ? 'border-[#FF7F00] bg-[#FF7F00]/10'
                        : 'border-white/10 bg-white/5 hover:border-white/30'
                    }`}
                    onClick={() => togglePickChar(c.id)}
                    data-testid={`character-card-${c.id}`}
                  >
                    <img
                      src={`${BACKEND_URL}${c.image_url}`}
                      alt={c.name}
                      className="w-12 h-12 rounded-full object-cover border border-white/20"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold truncate">{c.name}</div>
                      <div className="text-xs text-white/50 truncate">{c.description}</div>
                      <div className="text-[10px] uppercase tracking-wider text-[#0070FF] mt-0.5">
                        Voice: {c.voice}
                      </div>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteChar(c.id);
                      }}
                      className="opacity-0 group-hover:opacity-100 text-white/40 hover:text-red-400 transition-all"
                      data-testid={`delete-character-${c.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Recent projects */}
          <section data-testid="projects-list">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-white/70 mb-3">
              Recent Projects
            </h2>
            <div className="space-y-2">
              {projects.length === 0 && (
                <div className="text-xs text-white/40 border border-dashed border-white/15 rounded-lg p-4 text-center">
                  Generated projects will appear here.
                </div>
              )}
              {projects.slice(0, 12).map((p) => (
                <div
                  key={p.id}
                  className={`group p-3 rounded-lg border cursor-pointer transition-all ${
                    activeProject?.id === p.id
                      ? 'border-[#0070FF] bg-[#0070FF]/10'
                      : 'border-white/10 bg-white/5 hover:border-white/25'
                  }`}
                  onClick={() => setActiveProject(p)}
                  data-testid={`project-card-${p.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium truncate flex-1">{p.title}</div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteProject(p.id);
                      }}
                      className="opacity-0 group-hover:opacity-100 text-white/40 hover:text-red-400"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div className="text-[10px] text-white/40 mt-1 flex items-center gap-2">
                    <span
                      className={`inline-block w-1.5 h-1.5 rounded-full ${
                        p.status === 'complete'
                          ? 'bg-green-400'
                          : p.status === 'error'
                          ? 'bg-red-400'
                          : 'bg-yellow-400 animate-pulse'
                      }`}
                    />
                    {STATUS_LABELS[p.status] || p.status}
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>

        {/* Right column — generator + viewer */}
        <div className="lg:col-span-2 space-y-8">
          {/* Generator */}
          <section
            className="bg-white/5 border border-white/10 rounded-2xl p-6"
            data-testid="video-generator"
          >
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-5 h-5 text-[#FF7F00]" />
              <h2 className="text-lg font-semibold">Generate Reel (9:16)</h2>
            </div>

            <label className="block text-xs uppercase tracking-wider text-white/50 mb-1">
              Title
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., 30s patient testimonial"
              className="w-full mb-4 px-3 py-2 rounded-lg bg-black/30 border border-white/15 text-sm focus:outline-none focus:border-[#FF7F00]"
              data-testid="project-title-input"
            />

            <label className="block text-xs uppercase tracking-wider text-white/50 mb-1">
              Script
            </label>
            <textarea
              value={script}
              onChange={(e) => setScript(e.target.value)}
              rows={6}
              placeholder="Paste your full script here. The AI will split it into scenes."
              className="w-full mb-4 px-3 py-2 rounded-lg bg-black/30 border border-white/15 text-sm focus:outline-none focus:border-[#FF7F00] resize-y"
              data-testid="project-script-input"
            />

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-xs uppercase tracking-wider text-white/50 mb-1">
                  Mode
                </label>
                <div className="flex gap-2" data-testid="mode-toggle">
                  <button
                    type="button"
                    onClick={() => { setMode('slideshow'); setTotalSeconds(30); }}
                    className={`flex-1 px-3 py-2 rounded-lg text-xs font-semibold border transition-all ${
                      mode === 'slideshow'
                        ? 'bg-[#0070FF] border-[#0070FF] text-white'
                        : 'bg-black/30 border-white/15 text-white/60 hover:border-white/40'
                    }`}
                    data-testid="mode-slideshow-btn"
                  >
                    Slideshow
                    <div className="text-[10px] font-normal opacity-80">~$0.50 / fast</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => { setMode('sora'); setTotalSeconds(16); }}
                    className={`flex-1 px-3 py-2 rounded-lg text-xs font-semibold border transition-all ${
                      mode === 'sora'
                        ? 'bg-[#FF7F00] border-[#FF7F00] text-black'
                        : 'bg-black/30 border-white/15 text-white/60 hover:border-white/40'
                    }`}
                    data-testid="mode-sora-btn"
                  >
                    Sora 2 Pro
                    <div className="text-[10px] font-normal opacity-80">~$8 / cinematic</div>
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-white/50 mb-1">
                  Length
                </label>
                <select
                  value={totalSeconds}
                  onChange={(e) => setTotalSeconds(parseInt(e.target.value, 10))}
                  className="w-full px-3 py-2 rounded-lg bg-black/30 border border-white/15 text-sm"
                  data-testid="length-select"
                >
                  {mode === 'sora' ? (
                    <>
                      <option value={8}>8 seconds</option>
                      <option value={16}>16 seconds</option>
                      <option value={24}>24 seconds</option>
                      <option value={32}>32 seconds</option>
                    </>
                  ) : (
                    <>
                      <option value={15}>15 seconds</option>
                      <option value={20}>20 seconds</option>
                      <option value={30}>30 seconds</option>
                      <option value={45}>45 seconds</option>
                      <option value={60}>60 seconds</option>
                      <option value={90}>90 seconds</option>
                    </>
                  )}
                </select>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-xs uppercase tracking-wider text-white/50 mb-1">
                Selected characters ({pickedChars.length}/2)
              </label>
              <div className="flex flex-wrap gap-1">
                {pickedChars.length === 0 && (
                  <span className="text-xs text-white/40">Click character cards on the left</span>
                )}
                {pickedChars.map((id) => {
                  const c = characters.find((x) => x.id === id);
                  if (!c) return null;
                  return (
                    <span
                      key={id}
                      className="text-xs px-2 py-1 rounded-full bg-[#FF7F00]/20 border border-[#FF7F00]/40 text-[#FF7F00]"
                    >
                      {c.name}
                    </span>
                  );
                })}
              </div>
            </div>

            <button
              onClick={handleSubmit}
              disabled={submitting || !script.trim()}
              className="w-full py-3 rounded-lg bg-[#FF7F00] hover:bg-[#FF9933] disabled:bg-white/10 disabled:text-white/40 text-black font-semibold flex items-center justify-center gap-2 transition-colors"
              data-testid="generate-video-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Film className="w-4 h-4" />}
              Generate Video
            </button>
            <p className="text-[11px] text-white/40 mt-2">
              {mode === 'sora'
                ? 'Sora 2 Pro takes ~3–5 min per 8s clip. Voiceover is generated separately after.'
                : 'Slideshow generates Nano Banana images + Ken Burns motion + AI narration in ~30–60 seconds. Audio is included automatically.'}
            </p>
          </section>

          {/* Viewer */}
          {activeProject && (
            <section
              className="bg-white/5 border border-white/10 rounded-2xl p-6"
              data-testid="video-viewer"
            >
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold">{activeProject.title}</h3>
                  <div className="text-xs text-white/50">
                    {STATUS_LABELS[activeProject.status] || activeProject.status} •{' '}
                    {activeProject.progress || 0}%
                  </div>
                </div>
                <button
                  onClick={() => setActiveProject(null)}
                  className="text-white/40 hover:text-white"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* progress bar */}
              {activeProject.status !== 'complete' && activeProject.status !== 'error' && (
                <div className="w-full h-1.5 bg-white/10 rounded-full overflow-hidden mb-4">
                  <div
                    className="h-full bg-[#FF7F00] transition-all"
                    style={{ width: `${activeProject.progress || 0}%` }}
                  />
                </div>
              )}

              {activeProject.status === 'error' && (
                <div className="text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4">
                  {activeProject.error || 'Unknown error'}
                </div>
              )}

              {renderVideoUrl(activeProject) && (
                <video
                  key={renderVideoUrl(activeProject)}
                  src={renderVideoUrl(activeProject)}
                  controls
                  className="w-full max-h-[70vh] rounded-xl bg-black mx-auto"
                  style={{ aspectRatio: '9/16' }}
                  data-testid="video-player"
                />
              )}

              {/* Scenes breakdown */}
              {activeProject.scenes?.length > 0 && (
                <details className="mt-4">
                  <summary className="text-xs text-white/50 cursor-pointer hover:text-white">
                    View scene breakdown ({activeProject.scenes.length})
                  </summary>
                  <div className="mt-2 space-y-2">
                    {activeProject.scenes.map((s, i) => (
                      <div
                        key={i}
                        className="text-xs bg-black/30 border border-white/10 rounded-lg p-3"
                      >
                        <div className="font-semibold text-[#FF7F00] mb-1">Scene {i + 1}</div>
                        <div className="text-white/70 mb-1">{s.prompt}</div>
                        {s.dialogue && (
                          <div className="text-white/90 italic">"{s.dialogue}"</div>
                        )}
                      </div>
                    ))}
                  </div>
                </details>
              )}

              {/* Audio + download */}
              <div className="flex flex-wrap items-center gap-3 mt-5">
                {activeProject.mode !== 'slideshow' && (
                  <button
                    onClick={handleGenerateAudio}
                    disabled={
                      activeProject.status !== 'complete' ||
                      activeProject.status === 'adding_audio' ||
                      !activeProject.video_url
                    }
                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#0070FF] hover:bg-[#0055CC] disabled:bg-white/10 disabled:text-white/40 text-white text-sm font-semibold"
                    data-testid="generate-audio-btn"
                  >
                    {activeProject.status === 'adding_audio' ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <AudioLines className="w-4 h-4" />
                    )}
                    {activeProject.audio_url ? 'Regenerate Voiceover' : 'Generate Voiceover'}
                  </button>
                )}

                {renderAudioUrl(activeProject) && (
                  <a
                    href={renderAudioUrl(activeProject)}
                    download
                    className="flex items-center gap-2 px-4 py-2 rounded-lg border border-white/20 hover:border-white/40 text-sm"
                  >
                    <Download className="w-4 h-4" /> Audio (mp3)
                  </a>
                )}

                {renderVideoUrl(activeProject) && (
                  <a
                    href={renderVideoUrl(activeProject)}
                    download
                    className="flex items-center gap-2 px-4 py-2 rounded-lg border border-white/20 hover:border-white/40 text-sm"
                    data-testid="download-video-btn"
                  >
                    <Download className="w-4 h-4" /> Download mp4
                  </a>
                )}
              </div>
            </section>
          )}
        </div>
      </div>

      {/* Add character modal */}
      <AnimatePresence>
        {showCharModal && (
          <CharacterModal
            voices={voices}
            onClose={() => setShowCharModal(false)}
            onCreated={() => {
              setShowCharModal(false);
              loadCharacters();
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

const CharacterModal = ({ voices, onClose, onCreated }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [voice, setVoice] = useState('nova');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const onPick = (f) => {
    setFile(f);
    if (f) setPreview(URL.createObjectURL(f));
  };

  const submit = async () => {
    if (!name.trim() || !description.trim() || !file) {
      toast.error('Name, description and image are required');
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('name', name);
      fd.append('description', description);
      fd.append('voice', voice);
      fd.append('image', file);
      await axios.post(`${API}/characters`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Character created');
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Create failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.div
      className="fixed inset-0 z-[100] bg-black/70 flex items-center justify-center p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      data-testid="character-modal"
    >
      <motion.div
        className="bg-[#15151E] border border-white/10 rounded-2xl w-full max-w-md p-6"
        initial={{ scale: 0.95, y: 10 }}
        animate={{ scale: 1, y: 0 }}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">New Character</h3>
          <button onClick={onClose} className="text-white/40 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* image */}
        <label className="block text-xs uppercase tracking-wider text-white/50 mb-1">
          Reference Image
        </label>
        <label className="block w-full mb-4 cursor-pointer">
          <div className="border-2 border-dashed border-white/15 rounded-lg p-4 text-center hover:border-[#FF7F00] transition-colors">
            {preview ? (
              <img src={preview} alt="preview" className="mx-auto max-h-32 rounded" />
            ) : (
              <div className="flex flex-col items-center gap-1 text-white/50 text-xs">
                <Upload className="w-5 h-5" />
                Click to upload (PNG / JPG)
              </div>
            )}
          </div>
          <input
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="hidden"
            onChange={(e) => onPick(e.target.files?.[0] || null)}
            data-testid="character-image-input"
          />
        </label>

        <label className="block text-xs uppercase tracking-wider text-white/50 mb-1">Name</label>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full mb-3 px-3 py-2 rounded-lg bg-black/30 border border-white/15 text-sm"
          placeholder="Dr. Sarah"
          data-testid="character-name-input"
        />

        <label className="block text-xs uppercase tracking-wider text-white/50 mb-1">
          Description
        </label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          className="w-full mb-3 px-3 py-2 rounded-lg bg-black/30 border border-white/15 text-sm resize-y"
          placeholder="Cardiologist in her 30s, white coat, warm friendly expression"
          data-testid="character-desc-input"
        />

        <label className="block text-xs uppercase tracking-wider text-white/50 mb-1">Voice</label>
        <select
          value={voice}
          onChange={(e) => setVoice(e.target.value)}
          className="w-full mb-5 px-3 py-2 rounded-lg bg-black/30 border border-white/15 text-sm"
          data-testid="character-voice-select"
        >
          {voices.map((v) => (
            <option key={v.id} value={v.id}>
              {v.label}
            </option>
          ))}
        </select>

        <button
          onClick={submit}
          disabled={busy}
          className="w-full py-2.5 rounded-lg bg-[#FF7F00] hover:bg-[#FF9933] disabled:bg-white/10 disabled:text-white/40 text-black font-semibold flex items-center justify-center gap-2"
          data-testid="character-save-btn"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserIcon className="w-4 h-4" />}
          Save Character
        </button>
      </motion.div>
    </motion.div>
  );
};

export default VideoStudio;
